/**
 * 🌐 CONNECTIVITY MONITOR - UX para Apps Legacy
 * Gerencia estado offline, queue de ações e feedback visual
 */

import React, { useState, useEffect, useCallback } from 'react';
import { notification, message, Button, Spin } from 'antd';
import { WifiOutlined, DisconnectOutlined, SyncOutlined, CloudSyncOutlined } from '@ant-design/icons';

interface QueuedAction {
  id: string;
  type: 'api_call' | 'form_submit' | 'file_upload' | 'data_sync';
  payload: any;
  timestamp: number;
  retryCount: number;
  priority: 'low' | 'medium' | 'high';
}

interface ConnectivityState {
  isOnline: boolean;
  connectionType: 'fast' | 'slow' | 'offline';
  lastOnlineTime: number | null;
  queuedActions: QueuedAction[];
  syncInProgress: boolean;
}

const ConnectivityMonitor: React.FC = () => {
  const [state, setState] = useState<ConnectivityState>({
    isOnline: navigator.onLine,
    connectionType: 'fast',
    lastOnlineTime: navigator.onLine ? Date.now() : null,
    queuedActions: [],
    syncInProgress: false
  });

  /**
   * 🔍 DETECTAR QUALIDADE DA CONEXÃO
   */
  const detectConnectionQuality = useCallback(async (): Promise<'fast' | 'slow' | 'offline'> => {
    if (!navigator.onLine) return 'offline';

    try {
      const start = Date.now();
      
      // Test com endpoint leve da própria aplicação
      const response = await fetch('/favicon.ico', { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      const duration = Date.now() - start;
      
      if (!response.ok) return 'slow';
      
      // Classificar baseado na latência
      if (duration < 300) return 'fast';      // < 300ms = conexão rápida
      if (duration < 1000) return 'slow';     // < 1s = conexão lenta
      return 'slow';                          // > 1s = conexão muito lenta
      
    } catch (error) {
      return 'offline';
    }
  }, []);

  /**
   * 📱 FEEDBACK VISUAL PARA O USUÁRIO
   */
  const showConnectivityFeedback = useCallback((newState: ConnectivityState) => {
    if (!newState.isOnline) {
      notification.warning({
        key: 'connectivity',
        message: '📶 Sem Conexão',
        description: 'Você está offline. Suas ações não serão persistidas na aplicação.',
        duration: 0, // Manter até voltar online
        placement: 'topRight',
        icon: <DisconnectOutlined style={{ color: '#ff4d4f' }} />
      });
    } else {
      // Fechar todas as notificações (compatibilidade com versão antiga do AntD)
      notification.destroy();
      
      if (newState.queuedActions.length > 0) {
        notification.success({
          key: 'back-online',
          message: '🌐 Conectado!',
          description: `Sincronizando ${newState.queuedActions.length} ações pendentes...`,
          duration: 3,
          placement: 'topRight',
          icon: <CloudSyncOutlined style={{ color: '#52c41a' }} />
        });
      }
    }
  }, []);

  /**
   * 📋 GERENCIAR QUEUE DE AÇÕES OFFLINE
   */
  const queueAction = useCallback((action: Omit<QueuedAction, 'id' | 'timestamp' | 'retryCount'>) => {
    const queuedAction: QueuedAction = {
      ...action,
      id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0
    };

    setState(prev => ({
      ...prev,
      queuedActions: [...prev.queuedActions, queuedAction]
    }));

    // Feedback imediato
    message.info(`⏳ Ação salva para sincronização (${action.type})`, 2);
    
    return queuedAction.id;
  }, []);

  /**
   * 🔄 SINCRONIZAR AÇÕES PENDENTES
   */
  const syncQueuedActions = useCallback(async () => {
    if (state.queuedActions.length === 0 || !state.isOnline) return;

    setState(prev => ({ ...prev, syncInProgress: true }));

    const results = {
      success: 0,
      failed: 0,
      skipped: 0
    };

    // Processar por prioridade
    const sortedActions = [...state.queuedActions].sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

    for (const action of sortedActions) {
      try {
        // Simular processamento baseado no tipo
        await processQueuedAction(action);
        results.success++;
        
        // Remover da queue
        setState(prev => ({
          ...prev,
          queuedActions: prev.queuedActions.filter(a => a.id !== action.id)
        }));
        
      } catch (error) {
        console.error(`Erro ao sincronizar ação ${action.id}:`, error);
        
        // Incrementar retry count
        setState(prev => ({
          ...prev,
          queuedActions: prev.queuedActions.map(a => 
            a.id === action.id 
              ? { ...a, retryCount: a.retryCount + 1 }
              : a
          )
        }));
        
        // Remover se muitas tentativas falharam
        if (action.retryCount >= 3) {
          setState(prev => ({
            ...prev,
            queuedActions: prev.queuedActions.filter(a => a.id !== action.id)
          }));
          results.skipped++;
        } else {
          results.failed++;
        }
      }
    }

    setState(prev => ({ ...prev, syncInProgress: false }));

    // Feedback de resultado
    if (results.success > 0) {
      notification.success({
        message: '✅ Sincronização Concluída',
        description: `${results.success} ações sincronizadas com sucesso.`,
        duration: 3
      });
    }

    if (results.failed > 0 || results.skipped > 0) {
      notification.warning({
        message: '⚠️ Sincronização Parcial',
        description: `${results.failed} ações falharam, ${results.skipped} foram descartadas.`,
        duration: 5
      });
    }
  }, [state.queuedActions, state.isOnline]);

  /**
   * 🔧 PROCESSAR AÇÃO ESPECÍFICA
   */
  const processQueuedAction = async (action: QueuedAction): Promise<void> => {
    switch (action.type) {
      case 'api_call':
        await fetch(action.payload.url, action.payload.options);
        break;
      
      case 'form_submit':
        await fetch(action.payload.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action.payload.data)
        });
        break;
      
      case 'file_upload':
        await fetch(action.payload.uploadUrl, {
          method: 'PUT',
          body: action.payload.file
        });
        break;
      
      case 'data_sync':
        // Lógica customizada para sincronização de dados
        await fetch('/api/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action.payload)
        });
        break;
      
      default:
        throw new Error(`Tipo de ação não suportado: ${action.type}`);
    }
  };

  /**
   * 🎯 MONITORAR MUDANÇAS DE CONECTIVIDADE
   */
  useEffect(() => {
    const handleOnline = async () => {
      const connectionType = await detectConnectionQuality();
      
      const newState = {
        ...state,
        isOnline: true,
        connectionType,
        lastOnlineTime: Date.now()
      };
      
      setState(newState);
      showConnectivityFeedback(newState);
      
      // Auto-sync quando voltar online
      setTimeout(() => syncQueuedActions(), 1000);
    };

    const handleOffline = () => {
      const newState = {
        ...state,
        isOnline: false,
        connectionType: 'offline' as const
      };
      
      setState(newState);
      showConnectivityFeedback(newState);
    };

    // Event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Polling periódico da qualidade da conexão
    const connectionMonitor = setInterval(async () => {
      if (navigator.onLine) {
        const connectionType = await detectConnectionQuality();
        setState(prev => ({ ...prev, connectionType }));
      }
    }, 30000); // A cada 30 segundos

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectionMonitor);
    };
  }, [detectConnectionQuality, showConnectivityFeedback, syncQueuedActions]);

  /**
   * 🎨 INDICADOR VISUAL DE CONECTIVIDADE
   */
  const renderConnectivityIndicator = () => {
    const { isOnline, connectionType, queuedActions, syncInProgress } = state;

    if (!isOnline) {
      return (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          right: '20px', 
          backgroundColor: '#ff4d4f', 
          color: 'white', 
          padding: '8px 16px', 
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '12px'
        }}>
          <DisconnectOutlined /> Offline {queuedActions.length > 0 && `(${queuedActions.length} pendentes)`}
        </div>
      );
    }

    if (connectionType === 'slow') {
      return (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          right: '20px', 
          backgroundColor: '#faad14', 
          color: 'white', 
          padding: '8px 16px', 
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '12px'
        }}>
          <WifiOutlined /> Conexão Lenta
        </div>
      );
    }

    if (syncInProgress) {
      return (
        <div style={{ 
          position: 'fixed', 
          bottom: '20px', 
          right: '20px', 
          backgroundColor: '#1890ff', 
          color: 'white', 
          padding: '8px 16px', 
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '12px'
        }}>
          <Spin size="small" style={{ marginRight: '8px' }} />
          Sincronizando...
        </div>
      );
    }

    return null;
  };

  // Expor funções globalmente para outros componentes usarem
  useEffect(() => {
    (window as any).connectivityMonitor = {
      queueAction,
      isOnline: state.isOnline,
      connectionType: state.connectionType,
      queuedActions: state.queuedActions,
      syncNow: syncQueuedActions
    };
  }, [queueAction, state.isOnline, state.connectionType, state.queuedActions, syncQueuedActions]);

  return renderConnectivityIndicator();
};

export default ConnectivityMonitor; 